import React, { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { AlertTriangle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { processBulkOrders, ParsedOrder } from "@/lib/bulkOrderUtils";
import { useLanguage } from "@/contexts/LanguageContext";

interface SmartPasteTabProps {
  onParsed: (orders: ParsedOrder[], summary: any) => void;
  onProgress?: (progress: { completed: number; total: number }) => void;
  onContentChange?: (hasContent: boolean) => void;
}

export interface SmartPasteTabRef {
  hasContent: () => boolean;
  getEstimatedCount: () => number | undefined;
  parse: () => Promise<void>;
  cancel: () => void;
}

// Client-side safety timeout (90 seconds)
const CLIENT_TIMEOUT_MS = 90000;
// Large input warning threshold
const LARGE_INPUT_THRESHOLD = 20000;
// Very large input threshold (needs explicit warning)
const VERY_LARGE_INPUT_THRESHOLD = 30000;
// Estimate ~100 chars per order for estimation
const CHARS_PER_ORDER_ESTIMATE = 100;

export const SmartPasteTab = forwardRef<SmartPasteTabRef, SmartPasteTabProps>(
  ({ onParsed, onProgress, onContentChange }, ref) => {
    const [text, setText] = useState("");
    const { toast } = useToast();
    const { t } = useLanguage();
    const abortRef = useRef(false);

    // Notify parent when content changes
    const handleTextChange = (value: string) => {
      setText(value);
      onContentChange?.(value.trim().length > 0);
    };

    // Expose imperative methods to parent
    useImperativeHandle(ref, () => ({
      hasContent: () => text.trim().length > 0,
      getEstimatedCount: () => {
        if (!text.trim()) return undefined;
        return Math.max(1, Math.ceil(text.length / CHARS_PER_ORDER_ESTIMATE));
      },
      parse: handleParse,
      cancel: () => {
        abortRef.current = true;
      },
    }));

    const handleParse = async () => {
      if (!text.trim()) {
        toast({
          title: t("bulk.errors.noPaste"),
          description: t("bulk.errors.noPaste"),
          variant: "destructive"
        });
        return;
      }

      abortRef.current = false;
      
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          abortRef.current = true;
          reject(new Error("TIMEOUT"));
        }, CLIENT_TIMEOUT_MS);
      });

      try {
        const resultPromise = processBulkOrders('text', text, (completed, total) => {
          if (!abortRef.current) {
            onProgress?.({ completed, total });
          }
        });

        const result = await Promise.race([resultPromise, timeoutPromise]);

        if (abortRef.current) return;

        if (result.success && result.orders.length > 0) {
          onParsed(result.orders, result.summary);
          toast({
            title: t("common.success"),
            description: `${result.orders.length} ${t("orders.ordersLabel").toLowerCase()} (${(result.summary.processingTimeMs / 1000).toFixed(1)}s)`
          });
        } else {
          let errorMessage = result.error || t("bulk.errors.processingFailed");
          
          if (result.error === "RATE_LIMIT" || result.error?.includes("rate limit")) {
            errorMessage = t("bulk.errors.rateLimitExceeded") || "Too many requests. Please wait a minute and try again.";
          } else if (result.error === "CREDITS_REQUIRED" || result.error?.includes("credits")) {
            errorMessage = t("bulk.errors.creditsRequired") || "AI processing credits required. Please contact support.";
          }
          
          toast({
            title: t("bulk.errors.processingFailed"),
            description: errorMessage,
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Parse error:', error);
        
        const estimatedOrders = Math.ceil(text.length / CHARS_PER_ORDER_ESTIMATE);
        const recommendedMax = 20;
        
        let errorMessage: string;
        if (error instanceof Error && error.message === "TIMEOUT") {
          errorMessage = estimatedOrders > recommendedMax
            ? `Processing timed out. You have ~${estimatedOrders} orders. Try with ${recommendedMax} or fewer orders at a time.`
            : t("bulk.errors.timeout") || `Processing timed out after ${CLIENT_TIMEOUT_MS / 1000}s. Try with less text.`;
        } else {
          errorMessage = t("bulk.errors.processingFailed");
        }
        
        toast({
          title: t("bulk.errors.processingFailed"),
          description: errorMessage,
          variant: "destructive"
        });
      }
    };

    const isLargeInput = text.length > LARGE_INPUT_THRESHOLD;
    const isVeryLargeInput = text.length > VERY_LARGE_INPUT_THRESHOLD;
    const estimatedOrderCount = Math.ceil(text.length / CHARS_PER_ORDER_ESTIMATE);

    // Compact workbench layout matching ExcelUploadTab
    return (
      <div className="flex h-full flex-col gap-3">
        {/* Constraints line at top - same pattern as ExcelUploadTab */}
        <p className="text-xs text-muted-foreground">
          {t("bulk.constraintsPaste") || "Paste from Viber/WhatsApp • Multiple orders supported"} • {text.length} {t("bulk.characters") || "characters"}
          {isVeryLargeInput ? (
            <span className="text-destructive ml-2 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 inline" />
              ~{estimatedOrderCount} {t("common.orders") || "orders"} - {t("bulk.mayTakeLong") || "may take 1+ min"}
            </span>
          ) : isLargeInput ? (
            <span className="text-warning ml-2 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 inline" />
              ~{estimatedOrderCount} {t("common.orders") || "orders"}
            </span>
          ) : null}
        </p>

        {/* Textarea - compact, not filling entire space */}
        <Textarea
          placeholder={t("bulk.pasteTextarea") || "Paste chat text here..."}
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          className="min-h-[120px] resize-none font-mono text-sm border-2 bg-muted/20 rounded-xl focus-within:border-primary/50"
        />
      </div>
    );
  }
);

SmartPasteTab.displayName = "SmartPasteTab";
